<template>
  <div>
    <div class="filters" v-if="isAdmin && (customTypes.length || showDeleted)">
      <v-switch
        :label="$t('base.show_deleted')"
        v-model="showDeleted"
      />
    </div>

    <div v-if="customTypes.length" class="mobile-list">
      <div
        v-for="item in customTypes"
        :key="'custom-' + item.id"
        class="mobile-list__item"
        :class="{ 'mobile-list__item--deleted': item.deleted }"
        @click="!item.deleted && selectIngredientType(item.id)"
      >
        <div class="mobile-list__item-content">
          <div class="mobile-list__item-name">
            {{ item.name | shortstr }}
            <div v-if="item.deleted" class="type-badge type-badge--deleted">{{ $t('base.deleted') }}</div>
          </div>
        </div>
        <div class="mobile-list__item-action" v-if="!item.deleted">
          <v-icon font-size="1" icon="chevron-right" />
        </div>
      </div>
    </div>

    <ui-pagination
      v-if="showPagination"
      v-model="page"
      :page-count="pageCount"
      @page-change="pageChangeHandler"
    />

    <div class="empty-state" v-if="loaded && !hasTypes">
      <p>{{ $t('base.no_ingredient_types') }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import paginationMixin from '@/mixins/pagination.mixin'
import api from '@/api'
import UiPagination from '@/components/ui/ui-pagination/UiPagination.vue'

export default {
  name: 'IngredientTypesTableMobile',

  mixins: [paginationMixin],

  components: {
    UiPagination
  },

  data: () => ({
    loaded: false,
    showDeleted: false,
    userType: null
  }),

  async created () {
    try {
      const { data } = await api.account.get()
      this.userType = data.user_type
    } catch (error) {
      console.error('Failed to fetch user type:', error)
      this.userType = null
    }
  },

  async mounted () {
    const params = this.getParams()
    await this.setupPagination(api.ingredienttypes.list, params)

    this.loaded = true
  },

  computed: {
    ...mapGetters('ingredienttypes', [
      'ingredientTypes',
      'count'
    ]),

    customTypes () {
      return this.items || []
    },

    hasTypes () {
      return this.customTypes.length > 0
    },

    isAdmin () {
      return this.userType === 'admin'
    }
  },

  watch: {
    async showDeleted () {
      this.loaded = false
      const params = this.getParams()
      await this.setupPagination(api.ingredienttypes.list, params)
      this.loaded = true
    }
  },

  methods: {
    ...mapActions('ingredienttypes', [
      'loadIngredientTypes',
      'selectIngredientType'
    ]),

    getParams (page = null) {
      const params = {}

      if (!this.isAdmin) {
        params.deleted = false
      } else {
        params.deleted = this.showDeleted
      }

      if (this.$route.params.farm_id) {
        params.farm = this.$route.params.farm_id
      }

      if (page || this.page > 1) {
        params.page = page || this.page
      }

      return params
    },

    async loadDataList () {
      await this.loadIngredientTypes(this.getParams())
      this.loaded = true
    },

    async updateData () {
      await this.loadIngredientTypes(this.getParams())
      await this.setupPagination(api.ingredienttypes.list, this.getParams())
      this.loaded = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.filters {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
  padding: 0 1.6rem;
  
  ::v-deep .v-switch {
    &__label {
      font-size: 1.4rem;
      font-weight: 500;
      color: #666;
    }
  }
}

.mobile-list {
  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.6rem;
    background: $color-white;
    border-bottom: 1px solid $color-gray-light-2;
    cursor: pointer;
    transition: background 0.2s;

    &:active {
      background: $color-gray-light-1;
    }

    &--deleted {
      background: #ffebee;
      opacity: 0.7;

      .mobile-list__item-name {
        color: #c62828;
      }

      &:active {
        background: #ffcdd2;
      }
    }

    &-content {
      flex: 1;
      overflow: hidden;
    }

    &-name {
      font-size: 1.5rem;
      font-weight: 500;
      color: $color-black;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &-action {
      margin-left: 1rem;
      color: $color-gray;
    }
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  vertical-align: middle;

  &--deleted {
    background: #c62828;
    color: #fff;
    margin-left: 4px;
  }
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
}
</style>